import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="blog-post-loading">
        <p className="text-stone-500">Loading post...</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="blog-post-not-found">
        <h1 className="font-heading text-3xl font-bold text-stone-900">Post not found</h1>
        <p className="mt-4 text-stone-600">This post doesn't exist (yet).</p>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-700" data-testid="blog-post-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to the blog
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="blog-post-page">
      <Link
        to="/blog"
        data-testid="blog-post-back-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-stone-500 transition-colors hover:text-amber-700"
      >
        <ArrowLeft className="h-4 w-4" /> Back to the blog
      </Link>
      <div className="mt-10 flex flex-wrap items-center gap-3 text-xs">
        <span className="rounded-full bg-indigo-50 px-3 py-1 font-semibold text-indigo-800" data-testid="blog-post-category">
          {post.category}
        </span>
        <span className="font-mono text-stone-400" data-testid="blog-post-date">
          {format(new Date(post.created_at), "MMMM d, yyyy")} · {post.read_time}
        </span>
      </div>
      <h1 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-stone-900 leading-[1.15]" data-testid="blog-post-title">
        {post.title}
      </h1>
      {post.cover_image && (
        <img
          src={post.cover_image}
          alt={post.title}
          data-testid="blog-post-cover"
          className="mt-8 w-full aspect-video rounded-2xl object-cover border border-stone-200"
        />
      )}
      <div className="markdown-body mt-10" data-testid="blog-post-content">
        <ReactMarkdown>{post.content}</ReactMarkdown>
      </div>
    </article>
  );
}
